import { Component, OnInit } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { HistoryEntry } from 'src/app/services/ai-writing/ai-writing-history.service';

// Version timeline for one AI writing field: a clean list of every version with
// the full text of the selected one, and a plain-language action to bring an
// older version back into the field. Read-only — dismisses with the index the
// user chose to use (or nothing on close); the host owns applying it, so this
// modal never mutates the field or the history itself.
@Component({
  selector: 'app-ai-history-modal',
  templateUrl: './ai-history-modal.component.html',
  styleUrls: ['./ai-history-modal.component.scss'],
})
export class AiHistoryModalComponent implements OnInit {
  entries: HistoryEntry[] = [];
  index = 0; // the field's current (live) version
  fieldLabel = '';

  // Which entry the reading panel shows. Starts on the current version, then
  // follows the selected timeline node.
  selectedIndex = 0;

  constructor(
    private modalController: ModalController,
    private navParams: NavParams,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.entries = this.navParams.get('entries') || [];
    this.index = this.navParams.get('index') ?? 0;
    this.fieldLabel = this.navParams.get('fieldLabel') || '';
    this.selectedIndex = this.index;
  }

  // Newest first for the timeline; entry indices stay in original order.
  get orderedIndices(): number[] {
    return this.entries.map((_, i) => i).reverse();
  }

  get selectedEntry(): HistoryEntry | null {
    return this.entries[this.selectedIndex] ?? null;
  }

  get isSelectedCurrent(): boolean {
    return this.selectedIndex === this.index;
  }

  select(i: number): void {
    this.selectedIndex = i;
  }

  isCurrent(i: number): boolean {
    return i === this.index;
  }

  // Human-friendly version number: Original is v1, each AI edit increments.
  versionLabel(i: number): string {
    return `v${i + 1}`;
  }

  actionLabel(entry: HistoryEntry): string {
    switch (entry?.action) {
      case 'proofread':
        return 'Proofread';
      case 'rewrite':
        return 'Rewrite';
      case 'generate':
        return 'Generated';
      case 'adjust':
        return 'Adjusted';
      default:
        return 'Original';
    }
  }

  // 'generate' was given a description to draft from; 'adjust' was given an
  // instruction to act on. Calling both "prompt" hides which one this was.
  promptLabel(entry: HistoryEntry): string {
    return entry?.action === 'adjust' ? 'Your instruction' : 'Your prompt';
  }

  // Plain relative time, no date library. Words are translation keys so other
  // locales can localize the suffixes.
  relativeTime(at: number): string {
    const min = Math.floor((Date.now() - at) / 60000);
    if (min < 1) return this.translate.instant('just now');
    if (min < 60) return `${min}${this.translate.instant('m ago')}`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}${this.translate.instant('h ago')}`;
    return `${Math.floor(hr / 24)}${this.translate.instant('d ago')}`;
  }

  useThisVersion(): void {
    this.modalController.dismiss({ restoreIndex: this.selectedIndex });
  }

  close(): void {
    this.modalController.dismiss();
  }
}
